/**
 * **Package settings** — what the quoter is asked, and how the package prices
 * (`builder-engine-and-consistency.md` §7.1 item 1, Board 5 callout ①).
 *
 * Unchanged in content from the head card it replaces: Asked for, Measured
 * against, Default measure, Pricing (with a quantity's unit and default, and a
 * flat price, when those apply) and the five checkboxes — `Optional by
 * default` now reading `Starts as optional`. Props only; the page holds the
 * draft.
 */
import { useEffect, useMemo, useState } from 'react'
import { cn } from '../lib/cn.js'
import { FieldShell } from '../components/fields.js'
import { MenuSelect } from '../components/menu-select.js'
import { MoneyInput } from '../components/form/inputs.js'
import { Checkbox } from '../components/primitives.js'
import { controlClass } from '../components/form/field.js'
import { QtyInput } from '../builder/rows/line-row.js'
import { UnitInput } from '../builder/quantity/unit-input.js'
import { readNumber } from '../builder/quantity/tiers.js'
import { formatNumber } from '../lib/format.js'
import { STARTS_AS_OPTIONAL_LABEL } from '../builder/copy.js'
import type { PackageDraft, Unit } from '../builder/adapters/package.js'
import type { MeasurementType } from '../lib/catalog-types.js'

export function PackageSettingsCard({ draft, types, units, canEdit, onChange, bare }: {
  draft: PackageDraft
  types: MeasurementType[]
  units: Unit[]
  canEdit: boolean
  onChange: (over: Partial<PackageDraft>) => void
  /** Inside the phone's sheet: no card of its own, one column. */
  bare?: boolean
}) {
  /*
   * The unit list, plus whatever this package already holds — a package saved
   * before units were a catalog list carries a word that matches no `code`.
   */
  const unitOptions = useMemo(() => {
    const rows = units.map((one) => ({ value: one.code, label: `${one.name} (${one.code})` }))
    const held = draft.qtyUnit.trim()
    return held && !units.some((one) => one.code === held)
      ? [{ value: held, label: `${held} · not in the catalog` }, ...rows]
      : rows
  }, [units, draft.qtyUnit])

  // Typed text, so a decimal point survives being typed.
  const [flatText, setFlatText] = useState(() => (draft.flatPrice === null ? '' : String(draft.flatPrice)))
  useEffect(() => { setFlatText(draft.flatPrice === null ? '' : String(draft.flatPrice)) }, [draft.flatPrice])
  // "6,000", as Board 5 prints it; `readNumber` reads the comma back.
  const shownMeasure = (value: number | null) => (value === null ? '' : formatNumber(value))
  const [measureText, setMeasureText] = useState(() => shownMeasure(draft.defaultMeasure))
  useEffect(() => {
    if (readNumber(measureText) !== draft.defaultMeasure) setMeasureText(shownMeasure(draft.defaultMeasure))
  }, [draft.defaultMeasure])

  const wantsMeasure = draft.asks === 'measurement' || draft.asks === 'both'
  const wantsQty = draft.asks === 'quantity' || draft.asks === 'both'
  const type = types.find((one) => one.id === draft.measurementTypeId)

  const body = (
    <>
      <div className={cn('grid gap-x-4 gap-y-3',
        bare ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]')}>
        {/*
          * The package's name. Board 5 prints it in the page bar and draws no
          * field for it; a new package has to be named somewhere, so it is the
          * first field here (step 10's DEVIATION REQUEST).
          */}
        <FieldShell label="Name">
          <input
            value={draft.name}
            disabled={!canEdit}
            aria-label="Package name"
            onChange={(event) => onChange({ name: event.target.value })}
            className={controlClass()}
          />
        </FieldShell>

        <FieldShell
          label="The quoter is asked for"
          hint={draft.asks === 'none' ? 'a fixed job — nothing scales' : "lines marked 'per unit' scale from it"}
        >
          <MenuSelect
            label="The quoter is asked for"
            value={draft.asks}
            disabled={!canEdit}
            onChange={(value) => onChange({ asks: (value as PackageDraft['asks']) ?? 'none' })}
            size="field"
            className="w-full"
            options={[
              { value: 'quantity', label: 'Quantity' },
              { value: 'measurement', label: 'Measurement' },
              { value: 'both', label: 'Both' },
              { value: 'none', label: 'Nothing' },
            ]}
          />
        </FieldShell>

        {wantsQty && (
          <>
            <FieldShell label="Quantity unit">
              <MenuSelect
                label="Quantity unit"
                value={draft.qtyUnit || null}
                disabled={!canEdit}
                placeholder="unit"
                onChange={(value) => onChange({ qtyUnit: value ?? '' })}
                size="field"
                options={unitOptions}
              />
            </FieldShell>
            <FieldShell label="Default quantity">
              <QtyInput
                value={draft.defaultQty}
                onCommit={(value) => onChange({ defaultQty: value })}
                label="Default quantity"
                className="justify-end"
              />
            </FieldShell>
          </>
        )}

        {wantsMeasure && (
          <>
            <FieldShell label="Measured against" hint="matched to the property by type">
              <MenuSelect
                label="Measured against"
                value={draft.measurementTypeId}
                disabled={!canEdit}
                placeholder="Nothing"
                onChange={(value) => onChange({ measurementTypeId: value })}
                size="field"
                className="w-full"
                options={types.map((one) => ({ value: one.id, label: `${one.name} (${one.unit})` }))}
              />
            </FieldShell>
            <FieldShell label="Default measure">
              <UnitInput
                label="Default measure"
                value={measureText}
                unit={type?.unit}
                readOnly={!canEdit}
                onChange={(text) => {
                  setMeasureText(text)
                  onChange({ defaultMeasure: text.trim() === '' ? null : readNumber(text) })
                }}
              />
            </FieldShell>
          </>
        )}

        <FieldShell
          label="Pricing"
          hint={draft.pricing === 'flat'
            ? 'the customer sees one number; the lines still carry the cost'
            : 'the honest answer, and the default'}
        >
          <MenuSelect
            label="Pricing"
            value={draft.pricing}
            disabled={!canEdit}
            onChange={(value) => onChange({ pricing: (value as PackageDraft['pricing']) ?? 'from_lines' })}
            size="field"
            className="w-full"
            options={[
              { value: 'from_lines', label: 'From lines' },
              { value: 'flat', label: 'Flat' },
            ]}
          />
        </FieldShell>

        {draft.pricing === 'flat' && (
          <FieldShell label="Flat price">
            <MoneyInput
              value={flatText}
              disabled={!canEdit}
              aria-label="Flat price"
              placeholder="0.00"
              onChange={(event) => setFlatText(event.target.value)}
              onBlur={() => {
                const cleaned = flatText.replace(/[^0-9.]/g, '')
                const next = cleaned === '' ? null : Number(cleaned)
                onChange({ flatPrice: next !== null && Number.isFinite(next) ? next : null })
              }}
            />
          </FieldShell>
        )}
      </div>

      <div className={cn('flex flex-wrap gap-x-6 gap-y-2.5 pt-3.5', bare && 'flex-col')}>
        <Checkbox checked={draft.showAsOneLine} disabled={!canEdit}
          onChange={(event) => onChange({ showAsOneLine: event.target.checked })}
          label="Show as one package on the proposal" />
        <Checkbox checked={draft.editableAfterAdd} disabled={!canEdit}
          onChange={(event) => onChange({ editableAfterAdd: event.target.checked })}
          label="Quoter may edit lines after adding" />
        <Checkbox checked={draft.optionalDefault} disabled={!canEdit}
          onChange={(event) => onChange({ optionalDefault: event.target.checked })}
          label={STARTS_AS_OPTIONAL_LABEL} />
        <Checkbox checked={draft.rescaleOnMeasureChange} disabled={!canEdit}
          onChange={(event) => onChange({ rescaleOnMeasureChange: event.target.checked })}
          label="Re-scale when the measure changes" />
        <Checkbox checked={draft.active} disabled={!canEdit}
          onChange={(event) => onChange({ active: event.target.checked })}
          label="Offered" />
      </div>
    </>
  )

  if (bare) return <div data-package-settings="">{body}</div>
  return (
    <section data-package-settings="" className="shrink-0 rounded-record border border-line bg-surface px-5 pb-4 pt-4">
      <header className="mb-3.5 flex items-baseline gap-2.5">
        <b className="text-[16px] font-bold text-text">Package settings</b>
        <span className="text-[12.5px] text-text-muted">what the quoter is asked, and how the package prices</span>
      </header>
      {body}
    </section>
  )
}
